import type {
  FilterEducatorItem,
  FilterEnrollmentItem,
  FilterStatusItem,
  FilterSubscriptionItem,
  FilterTestTypeItem,
  GetFilterResponse
} from '@api/fetchers/goal/test-tab/types';

import { TEST_SERIES_FILTER } from '../Common/TestSeriesFilters';

import {
  API_TO_UI_SUBSCRIPTION,
  API_TO_UI_TEST_TYPE,
  UI_SUBSCRIPTION,
  getFreeSubscriptionTierLabel,
  getVisibleFiltersForCategoryTab
} from './constants';
import type {
  CategoryTabId,
  EducatorFilterOption,
  FilterId,
  FilterSelections,
  SimpleFilterOption,
  TestSeriesFilterConfigItem,
  UiSubscriptionId
} from './types';

const formatEducatorName = (educator: FilterEducatorItem): string => {
  const name = [educator.firstName, educator.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name || educator.username || 'Educator';
};

const mapSubscriptionItem = (
  item: FilterSubscriptionItem,
  isPrimeEnabled: boolean
): SimpleFilterOption => {
  const apiId = String(item.id);
  const uiId = (API_TO_UI_SUBSCRIPTION[
    apiId as keyof typeof API_TO_UI_SUBSCRIPTION
  ] || apiId) as UiSubscriptionId;

  return {
    id: uiId,
    label:
      uiId === UI_SUBSCRIPTION.PRIME
        ? getFreeSubscriptionTierLabel(isPrimeEnabled)
        : item.title || 'Subscription',
    apiId
  };
};

const mapTestTypeItem = (item: FilterTestTypeItem): SimpleFilterOption => {
  const apiId = String(item.id);
  const uiId = API_TO_UI_TEST_TYPE[apiId as keyof typeof API_TO_UI_TEST_TYPE];

  return {
    id: uiId || apiId,
    label: item.title || 'Test type',
    apiId
  };
};

const mapStatusItem = (item: FilterStatusItem): SimpleFilterOption => ({
  id: String(item.id),
  label: item.title || String(item.id),
  apiId: String(item.id)
});

const mapEnrollmentItem = (item: FilterEnrollmentItem): SimpleFilterOption => ({
  id: String(item.id),
  label: item.title || String(item.id),
  apiId: String(item.id)
});

const isKnownUiSubscription = (id: string): id is UiSubscriptionId =>
  (Object.values(UI_SUBSCRIPTION) as string[]).includes(id);

const buildSubscriptionFilterConfig = (
  response: GetFilterResponse | undefined,
  isPrimeEnabled: boolean
): TestSeriesFilterConfigItem => {
  const subscriptionItems = (response?.filters?.find(
    (filter) => filter.id === 'SUBSCRIPTION_TYPE'
  )?.items || []) as FilterSubscriptionItem[];

  const subscriptionOptions = subscriptionItems
    .map((item) => mapSubscriptionItem(item, isPrimeEnabled))
    .filter((option): option is SimpleFilterOption & { id: UiSubscriptionId } =>
      isKnownUiSubscription(option.id)
    );

  return {
    id: TEST_SERIES_FILTER.SUBSCRIPTION as FilterId,
    defaultLabel: 'Subscription type',
    allLabel: 'All subscription types',
    multipleLabel: (count: number) => `${count} subscription types`,
    options: subscriptionOptions
  };
};

type FilterIdWithoutSubscription = Exclude<FilterId, 'subscription'>;

const FILTER_CONFIG_BUILDERS: Record<
  FilterIdWithoutSubscription,
  (response: GetFilterResponse | undefined) => TestSeriesFilterConfigItem
> = {
  educator: (response) => {
    const educatorItems = (response?.filters?.find(
      (filter) => filter.id === 'EDUCATORS'
    )?.items || []) as FilterEducatorItem[];

    const educatorOptions: EducatorFilterOption[] = educatorItems.map(
      (educator) => ({
        id: educator.uid,
        name: formatEducatorName(educator),
        meta: educator.noOfTests ? `${educator.noOfTests} tests` : undefined,
        avatar: educator.avatar
      })
    );

    return {
      id: TEST_SERIES_FILTER.EDUCATOR as FilterId,
      defaultLabel: 'Educator',
      allLabel: 'all educators',
      multipleLabel: (count: number) => `${count} educators`,
      options: educatorOptions
    };
  },
  test_type: (response) => {
    const testTypeItems = (response?.filters?.find(
      (filter) => filter.id === 'TEST_TYPE'
    )?.items || []) as FilterTestTypeItem[];

    return {
      id: TEST_SERIES_FILTER.TEST_TYPE as FilterId,
      defaultLabel: 'Test type',
      allLabel: 'All test types',
      multipleLabel: (count: number) => `${count} test types`,
      options: testTypeItems.map(mapTestTypeItem)
    };
  },
  status: (response) => {
    const statusItems = (response?.filters?.find(
      (filter) => filter.id === 'STATUS'
    )?.items || []) as FilterStatusItem[];

    return {
      id: TEST_SERIES_FILTER.STATUS as FilterId,
      defaultLabel: 'Status',
      allLabel: 'All statuses',
      multipleLabel: (count: number) => `${count} statuses`,
      options: statusItems.map(mapStatusItem)
    };
  },
  enrollment: (response) => {
    const enrollmentItems = (response?.filters?.find(
      (filter) => filter.id === 'ENROLLMENT'
    )?.items || []) as FilterEnrollmentItem[];

    return {
      id: TEST_SERIES_FILTER.ENROLLMENT as FilterId,
      defaultLabel: 'Enrollment',
      allLabel: 'All enrollments',
      multipleLabel: (count: number) => `${count} enrollment types`,
      options: enrollmentItems.map(mapEnrollmentItem)
    };
  }
};

const ALL_FILTER_IDS: FilterId[] = [
  'subscription',
  'educator',
  'test_type',
  'status',
  'enrollment'
];

export interface ParsedFilterMetadata {
  filterConfig: TestSeriesFilterConfigItem[];
}

export const parseFilterMetadata = (
  response: GetFilterResponse | undefined,
  isPrimeEnabled = true
): ParsedFilterMetadata => ({
  filterConfig: ALL_FILTER_IDS.map((filterId) =>
    filterId === 'subscription'
      ? buildSubscriptionFilterConfig(response, isPrimeEnabled)
      : FILTER_CONFIG_BUILDERS[filterId](response)
  )
});

export const createEmptyFilterSelections = (): FilterSelections => ({
  subscription: [],
  educator: [],
  test_type: [],
  status: [],
  enrollment: []
});

export const getFilterConfigForCategoryTab = (
  categoryTabId: CategoryTabId,
  filterConfig: TestSeriesFilterConfigItem[],
  isLoggedIn = false
): TestSeriesFilterConfigItem[] => {
  const visibleFilterIds = getVisibleFiltersForCategoryTab(
    categoryTabId
  ).filter((filterId) => isLoggedIn || filterId !== 'enrollment');

  if (!visibleFilterIds.length) {
    return [];
  }

  const configById = filterConfig.reduce<
    Partial<Record<FilterId, TestSeriesFilterConfigItem>>
  >((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  return visibleFilterIds
    .map((filterId) => configById[filterId])
    .filter(
      (item): item is TestSeriesFilterConfigItem =>
        Boolean(item) && (item.id !== 'enrollment' || item.options.length > 0)
    );
};
